import React, { useEffect, useMemo } from 'react'
import { useState } from 'react'
import { useFetchList } from 'store'
import { useDeepCompareEffect, useLocation } from 'react-use'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import NotificationsView from './NotificationsView'
import { useInbox } from 'store'

import { matchPath } from 'react-router-dom'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'

import styles from 'controllers/InboxController/InboxController.module.sass'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import ShortDatePicker from 'components/ShortDatePicker/ShortDatePicker'
import useTermSearch from 'hooks/useTermSearch'
import TextInput from '@jimmyboys/liita-ui/dist/components/TextInput'

export const sortOptionsFunc = (i18n) => [
	{
		value: 'asc',
		text: i18n`Date, asc.`,
	},
	{
		value: 'desc',
		text: i18n`Date, desc.`,
	},
]

const InboxController = (props) => {
	const { isVisible, className, ...attributes } = props
	const { i18n } = useGlobal()
	const location = useLocation()
	const [processId, setProcessId] = useState(null)

	const { visibleData, inboxFilters, setInboxFilters, totalAppliedFilters } =
		useInbox()

	const {
		state,
		contents: [insuranceCompanies, insuranceBranches],
	} = useFetchList(
		['/metadata/insurance_companies', '/metadata/insurance_branches'],
		[
			(json) => {
				return {
					value: json.id.toString(),
					text: json.shortName,
				}
			},
			(json) => {
				return {
					value: json.id.toString(),
					text: json.name,
				}
			},
		]
	)

	const options = useMemo(() => {
		return [
			Array.isArray(insuranceBranches)
				? [
						{
							value: '',
							text: i18n`Any`,
						},
						...insuranceBranches,
				  ]
				: [],
			Array.isArray(insuranceCompanies)
				? [
						{
							value: '',
							text: i18n`Any`,
						},
						...insuranceCompanies,
				  ]
				: [],
		]
	}, [i18n, insuranceCompanies, insuranceBranches])

	const sortOptions = sortOptionsFunc(i18n)
	useDeepCompareEffect(() => {
		if (
			visibleData &&
			(
				visibleData?.filteredNotifications || visibleData?.notifications
			).find((elem) => elem.closed)
		) {
		}
	}, [visibleData?.filteredNotifications ?? visibleData?.notifications ?? {}])

	useEffect(() => {
		const match = matchPath(location?.pathname, {
			path: '/clinical-processes/:clinicalProcessId',
			strict: false,
			exact: false,
		})

		if (match?.params?.clinicalProcessId) {
			setProcessId((prevProcessId) =>
				prevProcessId !== match?.params?.clinicalProcessId
					? match?.params?.clinicalProcessId
					: prevProcessId
			)
		} else {
			setProcessId(null)
		}
	}, [location?.pathname])

	const filterValuesList = useMemo(() => {
		const branchItems = inboxFilters.branch?.length
			? options[0].map((elem) => {
					return inboxFilters.branch?.includes(elem.value)
						? { ...elem, $isSelected: true }
						: elem
			  })
			: [{ ...options[0][0], $isSelected: true }, ...options[0].slice(1)]

		const insuranceItems = inboxFilters.insuranceCompany?.length
			? options[1].map((elem) => {
					return inboxFilters.insuranceCompany?.includes(elem.value)
						? { ...elem, $isSelected: true }
						: elem
			  })
			: [{ ...options[1][0], $isSelected: true }, ...options[1].slice(1)]

		return [branchItems, insuranceItems]
	}, [inboxFilters, options])

	const sortList = useMemo(() => {
		return sortOptions.map((elem) => {
			return inboxFilters.sort === elem.value
				? { ...elem, $isSelected: true }
				: elem
		})
	}, [inboxFilters.sort, sortOptions])

	const titles = [i18n`branch`, i18n`insuranceCompany`]
	const filteredNotificationsByProcess = (
		visibleData?.notifications ?? []
	).filter((notification) => notification.processId === processId)

	const { searchMatches, setQuery } = useTermSearch(
		[
			...(visibleData?.filteredNotifications ??
				visibleData?.notifications ??
				[]),
		].filter((notification) => notification.processId !== processId || ''),
		['text', 'creationUser.name']
	)

	return (
		<>
			<div {...attributes} className={`${className || ''}`}>
				{isVisible && (
					<main className={`${styles['main']}`}>
						<div className={styles['actions-container']}>
							<div className={styles['search-input']}>
								<TextInput
									placeholder={i18n`Search`}
									className={styles['text-input']}
									debounce={200}
									onDebounce={({ target: { value } }) => {
										setQuery(value)
									}}
								/>
								<Icon
									className={styles['input-icon']}
									name='loupe'
									width='18px'
									height='18px'
								/>
							</div>
							<HStack gap='xsmall' justify='space-between'>
								<div className={styles['filter-content']}>
									<ModalOptions
										groups={filterValuesList}
										titles={titles}
										value={Object.values(inboxFilters)}
										closeAfterSelect={false}
										onSelect={(item, idx) => {
											const filter =
												idx === 0
													? 'branch'
													: 'insuranceCompany'

											if (!item.value) {
												setInboxFilters((prev) => {
													return {
														...prev,
														[filter]: [],
													}
												})
											} else {
												setInboxFilters((prev) => {
													const itemIndex = prev[
														filter
													].findIndex(
														(f) => f === item.value
													)

													if (itemIndex >= 0) {
														return {
															...prev,
															[filter]: [
																...prev[
																	filter
																].slice(
																	0,
																	itemIndex
																),
																...prev[
																	filter
																].slice(
																	itemIndex +
																		1
																),
															],
														}
													} else {
														return {
															...prev,
															[filter]: [
																...prev[filter],
																item.value,
															],
														}
													}
												})
											}
										}}
									>
										<ActionButton
											className={styles['action-button']}
											icon='filter'
										/>
									</ModalOptions>
									{totalAppliedFilters > 0 && (
										<small
											className={styles['filter-counter']}
										>
											{totalAppliedFilters > 9
												? '+9'
												: totalAppliedFilters}
										</small>
									)}
								</div>
								<ModalOptions
									options={sortList}
									title={i18n`Sort Date`}
									value={
										inboxFilters.sort
											? inboxFilters.sort
											: sortOptions[0].value
									}
									onSelect={(item) => {
										setInboxFilters((prev) => ({
											...prev,
											sort: item.value,
										}))
									}}
								>
									<ActionButton
										className={styles['action-button']}
										icon='sort'
									/>
								</ModalOptions>
								<div>
									<ShortDatePicker
										onChange={(data) => {
											if (data.isValid()) {
												setInboxFilters((prev) => ({
													...prev,
													date: data.toISOString(),
												}))
											} else {
												setInboxFilters((prev) => ({
													...prev,
													date: undefined,
												}))
											}
										}}
									/>
									{inboxFilters?.date && (
										<small
											className={styles['filter-counter']}
										>
											1
										</small>
									)}
								</div>
							</HStack>
						</div>
						<NotificationsView
							className={styles['container']}
							data={searchMatches}
							processId={processId}
							processData={filteredNotificationsByProcess}
							state={state}
						/>
					</main>
				)}
			</div>
		</>
	)
}

export default InboxController
